import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Logo } from "../../components/Logo";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

const colorFor = (n) => {
  if (n === null) return "#e5e7eb";
  if (n <= 6) return "#dc2626";
  if (n <= 8) return "#ca8a04";
  return "#16a34a";
};

export default function NpsResponder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [nota, setNota] = useState(null);
  const [comentario, setComentario] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/portal/tickets/${id}`).then((r) => setTicket(r.data.ticket)).catch(() => {});
  }, [id]);

  const enviar = async () => {
    if (nota === null) {
      toast.error("Selecione uma nota de 0 a 10");
      return;
    }
    setBusy(true);
    try {
      await api.post("/portal/nps", { ticket_id: id, nota, comentario });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-8 text-center" data-testid="nps-thanks">
          <div
            className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-white mb-4"
            style={{ background: "#16a34a" }}
          >
            <CheckCircle2 size={32} />
          </div>
          <h1 className="text-2xl font-display font-bold mb-2" style={{ color: "#1B3A6B" }}>
            Obrigado pela avaliação!
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            Sua opinião é muito importante para continuarmos melhorando o atendimento.
          </p>
          <Button
            onClick={() => nav("/cliente/chamados")}
            data-testid="nps-back-btn"
            className="text-white"
            style={{ background: "#F97316" }}
          >
            Voltar aos meus chamados
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full p-6 sm:p-8" data-testid="nps-page">
        <div className="flex flex-col items-center mb-6">
          <Logo size="sm" />
        </div>
        <h1 className="text-xl sm:text-2xl font-display font-bold text-center mb-1" style={{ color: "#1B3A6B" }}>
          Como você avalia o atendimento?
        </h1>
        {ticket && (
          <p className="text-center text-xs text-gray-500 mb-6">
            Chamado #{ticket.id.slice(0, 6).toUpperCase()} · {ticket.tipo}
            {ticket.descricao ? ` · ${ticket.descricao.slice(0, 60)}` : ""}
          </p>
        )}

        <div className="grid grid-cols-11 gap-1 sm:gap-2 mb-6" data-testid="nps-scale">
          {Array.from({ length: 11 }, (_, i) => (
            <button
              key={i}
              onClick={() => setNota(i)}
              data-testid={`nps-nota-${i}`}
              className="aspect-square rounded-md text-sm sm:text-base font-bold transition border-2"
              style={{
                background: nota === i ? colorFor(i) : "#fff",
                color: nota === i ? "#fff" : colorFor(i),
                borderColor: colorFor(i),
              }}
            >
              {i}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-gray-400 mb-6 px-1">
          <span>Pouco provável</span>
          <span>Muito provável</span>
        </div>

        <div className="mb-4">
          <label className="text-sm font-medium text-gray-700">
            Quer deixar um comentário? (opcional)
          </label>
          <Textarea
            data-testid="nps-comentario"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            rows={3}
            className="mt-1.5"
            placeholder="Conte como foi sua experiência..."
          />
        </div>

        <Button
          onClick={enviar}
          disabled={busy || nota === null}
          data-testid="nps-submit"
          className="w-full text-white h-11 font-semibold"
          style={{ background: "#F97316" }}
        >
          {busy && <Loader2 size={14} className="mr-2 animate-spin" />}
          Enviar avaliação
        </Button>
      </Card>
    </div>
  );
}
